/**
 * Replaces numerical segments in a URL path with the placeholder `{id}` so
 * errors that vary only by entity ID get grouped. Numbers immediately after
 * `/api/` are preserved (they are API versions, not IDs). Query parameter
 * values that are purely numeric or ISO datetime-shaped are also replaced,
 * and credential-bearing parameters are replaced with `{redacted}` whatever
 * the caller asks to preserve.
 *
 * Same-origin and relative URLs are reduced to path + query so they group
 * across hosts; cross-origin URLs keep their scheme + host so third-party
 * endpoints stay distinguishable from our own API paths.
 *
 * Example:
 *   /api/3/media2/85994393674/view?user=123 -> /api/3/media2/{id}/view?user={id}
 */
// Values of these query parameters vary per request (video ids, IPs, free text,
// upload tokens, filenames) and are replaced regardless of format.
const volatileQueryParams = [
	"contentIds",
	"ipAddress",
	"query",
	"meetingId",
	"filename",
	"uploadId",
];

const isoDateTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

const REDACTED = "{redacted}";

const credentialQueryParam =
	/token|secret|password|passwd|signature|credential|api[-_]?key|access[-_]?key|auth|jwt|^(sig|code|key|session|session_?id|sid|ticket|otp|nonce|pwd|pass|bearer)$|^x-amz-/i;

// A value that is itself a URL or path with a query string.
const nestedUrl = /^(?:[a-z][a-z\d+.-]*:\/\/|\/)[^?#]*\?/i;

// A numeric `code` is an error code (Redux, HTTP), not an OAuth authorization code.
export const isCredentialQueryParam = (key: string, value = ""): boolean =>
	credentialQueryParam.test(key) && !(/^code$/i.test(key) && /^\d+$/.test(value));

const decodeKey = (key: string): string => {
	try {
		return decodeURIComponent(key);
	} catch {
		return key;
	}
};

// `key=value` pairs in free text, plain or percent-encoded.
export const redactCredentialsInText = (text: string): string =>
	text
		.replace(
			/([?&;]|^|\s)([^=&?#\s"'<>]+)=([^&#\s"'<>]*)/g,
			(match, lead: string, key: string, value: string) =>
				isCredentialQueryParam(decodeKey(key), value) ? `${lead}${key}=${REDACTED}` : match
		)
		.replace(
			/(%3F|%26)([^%&=\s"'<>]+)%3D([^%&\s"'<>]*)/gi,
			(match, lead: string, key: string, value: string) =>
				isCredentialQueryParam(decodeKey(key), value) ? `${lead}${key}%3D${REDACTED}` : match
		);

export const generalizeUrl = (url: string, preserveQueryParams: readonly string[] = []): string => {
	if (!url) return "";

	let urlObj: URL;
	try {
		urlObj = new URL(url, "http://dummy.com/");
	} catch {
		return url;
	}

	let originPrefix = "";
	const isProtocolRelative = url.startsWith("//");
	const hasScheme = /^[a-z][a-z\d+.-]*:/i.test(url);
	if (urlObj.host && (hasScheme || isProtocolRelative)) {
		const loc = typeof window !== "undefined" ? window.location : undefined;
		if (isProtocolRelative) {
			if (urlObj.host !== loc?.host) originPrefix = `//${urlObj.host}`;
		} else if (urlObj.origin !== loc?.origin) {
			originPrefix = `${urlObj.protocol}//${urlObj.host}`;
		}
	}

	const originalSegments = urlObj.pathname.split("/");
	const generalizedSegments = originalSegments.map((segment, index) => {
		if (segment === "") return segment;
		// A comma-separated list of ids collapses into a single placeholder.
		if (/^\d+(?:,\d+)*$/.test(segment)) {
			if (originalSegments[index - 1]?.toLowerCase() === "api") return segment;
			return "{id}";
		}
		if (/^[a-f\d]{32,}$/i.test(segment)) return "{id}";
		return segment;
	});

	const generalizedPath = generalizedSegments.join("/") || "/";

	const generalizedSearch = new URLSearchParams();
	for (const [key, value] of urlObj.searchParams.entries()) {
		if (isCredentialQueryParam(key, value)) {
			generalizedSearch.append(key, REDACTED);
			continue;
		}
		if (nestedUrl.test(value)) {
			generalizedSearch.append(key, generalizeUrl(value));
			continue;
		}
		const generalize =
			!preserveQueryParams.includes(key) &&
			(volatileQueryParams.includes(key) ||
				(urlObj.pathname === "/api/5/bulbs/community" && key === "name") ||
				isoDateTime.test(value) ||
				/^\d+(?:,\d+)*$/.test(value));
		generalizedSearch.append(key, generalize ? "{id}" : redactCredentialsInText(value));
	}
	const search = generalizedSearch.toString();

	const combined = originPrefix + generalizedPath + (search ? "?" + search : "");
	try {
		return decodeURIComponent(combined);
	} catch {
		return combined;
	}
};
